/**
 * The loopback sync peer, from the app's side (RFC 0310).
 *
 * The desktop shell tells the window where the daemon's sync socket is, and the
 * tab passes it on to the shared repo worker, which connects to it beside the
 * relay. A plain browser tab is never told, so it never tries: without the
 * shell there is no daemon on this machine to find, and a socket retrying
 * forever against nothing would be noise in every console.
 *
 * WHY THE SHELL AND NOT WORLD PRESENCE. Presence arrives over the relay, which
 * is exactly the path that fails. The shell is on the same machine and already
 * hands the window local facts this way (`mod:local-harnesses`).
 */

/** The global the shell sets, and the event it fires when it does. */
export const LOCAL_SYNC_GLOBAL = '__modLocalSync'
export const LOCAL_SYNC_EVENT = 'mod:local-sync'

/** What the shell publishes. */
export interface LocalSyncOffer {
  url: string
}

/**
 * The URL to connect to, or null if this is not one.
 *
 * Loopback, the sync path, a port, nothing else. The value comes from the page
 * context, which the shell writes into but so could any script running in it, so
 * the worker never connects to something it was merely told to.
 */
export function localSyncUrl(offer: unknown): string | null {
  const raw =
    offer && typeof offer === 'object' && 'url' in offer
      ? (offer as { url: unknown }).url
      : undefined
  if (typeof raw !== 'string') return null
  let parsed: URL
  try {
    parsed = new URL(raw)
  } catch {
    return null
  }
  if (parsed.protocol !== 'ws:') return null
  if (parsed.hostname !== '127.0.0.1') return null
  if (!parsed.port) return null
  if (parsed.pathname !== '/sync') return null
  if (parsed.search || parsed.hash || parsed.username) return null
  return `ws://127.0.0.1:${parsed.port}/sync`
}

/**
 * Hand the shell's offer to the worker, now and whenever the shell repeats it.
 * Returns the unsubscribe. Does nothing outside a window.
 */
export function forwardLocalSync(send: (url: string) => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const offer = (value: unknown) => {
    const url = localSyncUrl(value)
    if (url) send(url)
  }
  offer((globalThis as Record<string, unknown>)[LOCAL_SYNC_GLOBAL])
  const onEvent = (e: Event) => offer((e as CustomEvent).detail)
  window.addEventListener(LOCAL_SYNC_EVENT, onEvent)
  return () => window.removeEventListener(LOCAL_SYNC_EVENT, onEvent)
}
